import { useEffect, useMemo, useState } from 'react';
import {
  Table, Input, Button, Tag, Space, Typography, Card, Alert, message, Modal, Select, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CalendarClock, Pencil, History, RefreshCw } from 'lucide-react';
import { api, fmt } from '../api';

const { Title, Text } = Typography;

type Row = {
  mcht_id: string;
  contract_end_date: string;
  decision: 'renew' | 'end';
  reason: string | null;
  ended_at: string | null;
  source: string | null;
  updated_at: string;
  updated_by: string | null;
  merchant_name: string;
  category: 'repay' | 'normal' | 'bad';
  cycle_started_at: string | null;
  start_date: string;
  term_days: number;
  purchase_amount: number;
  fee_rate: number;
  cumulative_repaid: number;
  remaining_amount: number;
};

type Event = {
  id: number;
  event_type: 'end' | 'renew_keep' | 'reseed';
  event_date: string;
  prev_cycle_started_at: string | null;
  new_cycle_started_at: string | null;
  cumulative_at_event: number | null;
  remaining_at_event: number | null;
  reason: string | null;
  triggered_by: string;
  created_at: string;
};

function todayKstStr(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  return kst.toISOString().slice(0, 10);
}

type PreviewCandidate = {
  mcht_id: string; merchant_name: string; contract_end_date: string; reason: string | null;
  purchase_amount: number; cumulative_repaid: number; remaining_amount: number;
  skip_reason?: string;
};
type PreviewData = {
  today: string; total: number;
  eligible_count: number; skipped_count: number;
  eligible: PreviewCandidate[]; skipped: PreviewCandidate[];
};
type RunResult = {
  ok: true; today: string; ended_count: number; skipped_count: number;
  ended: string[]; skipped: { mid: string; reason: string }[];
  triggered_by: string;
};

export default function Renewals() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [editDecision, setEditDecision] = useState<'renew' | 'end'>('renew');
  const [editReason, setEditReason] = useState<string>('');
  const [editEndDate, setEditEndDate] = useState<string>('');
  const [editTermDays, setEditTermDays] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [eventsFor, setEventsFor] = useState<Row | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [filter, setFilter] = useState<'all' | 'renew' | 'end' | 'ended'>('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api<{ data: Row[] }>('GET', '/merchant-renewals');
      setRows(r.data ?? []);
    } catch (e: any) {
      setErr(e?.message ?? '조회 실패');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const today = todayKstStr();

  const filtered = useMemo(() => {
    let r = rows;
    if (filter === 'renew') r = r.filter(x => x.decision === 'renew' && !x.ended_at);
    else if (filter === 'end') r = r.filter(x => x.decision === 'end' && !x.ended_at);
    else if (filter === 'ended') r = r.filter(x => !!x.ended_at);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(x =>
        x.merchant_name?.toLowerCase().includes(q) || x.mcht_id?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, filter, search]);

  // 시작일 + 이용기간(term_days) → 계약 종료일 계산.
  // 시작일 포함 계산: end = start + term - 1 (기존 정산 엔진과 동일하게 KST 날짜 산정).
  const computeEndDate = (startDate: string, termDays: number): string => {
    if (!startDate || !Number.isFinite(termDays) || termDays <= 0) return '';
    const d = new Date(startDate + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + Number(termDays) - 1);
    return d.toISOString().slice(0, 10);
  };
  // 시작일 + 종료일 → 이용기간 (역산). start=A, end=B, term = B - A + 1.
  const computeTermDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    const a = new Date(startDate + 'T00:00:00Z').getTime();
    const b = new Date(endDate + 'T00:00:00Z').getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
    return Math.round((b - a) / 86400_000) + 1;
  };

  const openEdit = (r: Row) => {
    setEditing(r);
    setEditDecision(r.decision);
    setEditReason(r.reason ?? '');
    setEditEndDate(r.contract_end_date);
    // 저장된 term_days가 우선 (계약기간 원장). 없으면 시작일+종료일로 역산.
    // 시드 데이터가 term_days와 contract_end_date 불일치인 경우도 있으니 사용자에게 그대로 노출해 정정 가능.
    setEditTermDays(r.term_days > 0 ? r.term_days : computeTermDays(r.start_date, r.contract_end_date));
  };
  const closeEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    // 종료일 사전 검증 — 사용자가 실제로 종료일을 변경한 경우에만 강제.
    // (기존 데이터가 이미 시작일 < 종료일 조건을 위반해도 결정·사유만 수정할 수 있게 허용.)
    const endDateChanged = editEndDate !== editing.contract_end_date;
    if (endDateChanged && editEndDate && editing.start_date && editEndDate < editing.start_date) {
      message.error(`종료일은 시작일(${editing.start_date})보다 빠를 수 없습니다.`);
      return;
    }
    // 종료 결정인데 사유 미입력 시 다시 확인.
    if (editDecision === 'end' && !editReason.trim()) {
      if (!confirm('종료 결정에 대한 사유가 입력되지 않았습니다.\n\n추후 이력 확인을 위해 사유 입력이 권장됩니다.\n그래도 저장하시겠습니까?')) return;
    }
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {
        decision: editDecision,
        reason: editReason || null,
      };
      // 종료일은 실제 변경 시에만 전송 — 기존 값 재전송으로 서버 검증에 걸리는 것을 피함.
      if (endDateChanged) patch.contract_end_date = editEndDate;
      await api('PATCH', `/merchant-renewals/${encodeURIComponent(editing.mcht_id)}`, patch);
      // 종료 선택 + 잔여액 남아있는 케이스 안내 (자동 종료 조건 미충족 안내).
      if (editDecision === 'end' && editing.remaining_amount > 0) {
        message.warning(`저장됨. 다만 잔여액 ${editing.remaining_amount.toLocaleString('ko-KR')}원이 남아 있어 자동 종료 대상이 아닙니다. 잔여 0이 될 때 자동 종료됩니다.`);
      } else {
        message.success('저장됨');
      }
      await load();
      closeEdit();
    } catch (e: any) {
      const body = e?.body && typeof e.body === 'object' ? e.body : {};
      if (body.error === 'invalid_date_range') message.error(body.message ?? '종료일이 시작일보다 빠를 수 없습니다.');
      else message.error(e?.message ?? '저장 실패');
    } finally { setSaving(false); }
  };

  const openEvents = async (r: Row) => {
    setEventsFor(r); setEvents([]);
    try {
      const res = await api<{ data: Event[] }>('GET', `/merchant-renewals/${encodeURIComponent(r.mcht_id)}/events`);
      setEvents(res.data ?? []);
    } catch { setEvents([]); }
  };

  // 자동 종료 미리보기 오픈 — 대상 목록/제외 목록을 사전 확인 후 실행.
  const openAutoEndPreview = async () => {
    setAutoLoading(true);
    setRunResult(null);
    try {
      const r = await api<PreviewData>('GET', '/merchant-renewals/auto-end-preview');
      setPreview(r);
    } catch (e: any) {
      message.error(e?.message ?? '미리보기 조회 실패');
    } finally { setAutoLoading(false); }
  };

  const runAutoEnd = async () => {
    if (autoRunning || !preview) return;
    if (preview.eligible_count === 0) {
      message.info('실행 대상이 없습니다.');
      return;
    }
    setAutoRunning(true);
    try {
      const r = await api<RunResult>('POST', '/merchant-renewals/run-auto-end', {});
      setRunResult(r);
      setPreview(null);
      await load();
    } catch (e: any) {
      message.error(e?.message ?? '실행 실패');
    } finally { setAutoRunning(false); }
  };

  const statusOf = (r: Row): { tag: string; color: string } => {
    if (r.ended_at) return { tag: '종료완료', color: 'default' };
    const dueArrived = r.contract_end_date <= today;
    if (r.decision === 'end') {
      if (r.remaining_amount > 0) return { tag: dueArrived ? '종료대기(잔여)' : '종료 예정', color: 'gold' };
      return dueArrived ? { tag: '자동종료 후보', color: 'volcano' } : { tag: '종료 예정', color: 'orange' };
    }
    return dueArrived ? { tag: '갱신중', color: 'blue' } : { tag: '진행중', color: 'green' };
  };

  const cols: ColumnsType<Row> = [
    {
      title: 'MID', dataIndex: 'mcht_id', key: 'mid', width: 140,
      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span>,
    },
    { title: '가맹점명', dataIndex: 'merchant_name', key: 'name', ellipsis: true,
      render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: '시작일', dataIndex: 'start_date', key: 'start', width: 110,
      render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt.date(v)}</span> },
    { title: '계약 종료 예정일', dataIndex: 'contract_end_date', key: 'end', width: 130,
      render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }} title="원 엑셀 시드에서 가져온 계약 종료 예정 일자">{fmt.date(v)}</span> },
    { title: '당일 잔여액', dataIndex: 'remaining_amount', key: 'remain', width: 130, align: 'right',
      render: (v: number) => v === 0
        ? <Text style={{ color: 'var(--leaf-700)', fontWeight: 600 }}>0원</Text>
        : <Text>{fmt.won(v)}원</Text>
    },
    { title: '결정', dataIndex: 'decision', key: 'decision', width: 80,
      render: (v: string) => v === 'end'
        ? <Tag color="orange">종료</Tag>
        : <Tag color="green">갱신</Tag>
    },
    { title: '상태', key: 'status', width: 130,
      render: (_, r) => { const s = statusOf(r); return <Tag color={s.color}>{s.tag}</Tag>; }
    },
    { title: '사유', dataIndex: 'reason', key: 'reason', ellipsis: true,
      render: v => v ? <span style={{ fontSize: 12 }}>{v}</span> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text> },
    {
      title: '작업', key: 'action', width: 130, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={r.ended_at ? '이미 종료 — 수정 불가' : '결정·사유·종료일 수정'}>
            <Button size="small" icon={<Pencil size={12} />}
                    disabled={!!r.ended_at}
                    onClick={() => openEdit(r)}>수정</Button>
          </Tooltip>
          <Tooltip title="이벤트 이력">
            <Button size="small" icon={<History size={12} />} onClick={() => openEvents(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <CalendarClock size={20} style={{ verticalAlign: -3, marginRight: 8 }} />
            계약 갱신/종료
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            기본값 = 갱신. 종료일 도래 + 잔여 0 + 종료 결정 시 자동 종료 처리됩니다. 잔여가 있으면 종료 불가(그대로 둠).
          </Text>
        </div>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={load} loading={loading}>새로고침</Button>
          <Button type="primary" onClick={openAutoEndPreview} loading={autoLoading}>자동 종료 대상 확인</Button>
        </Space>
      </div>

      {err && <Alert type="error" message={err} showIcon style={{ marginBottom: 12 }} />}

      {/* 문서 09-01 §11: 요약 카드 (전체/결정/상태별 건수, 클릭 시 필터) */}
      {(() => {
        const cnts = {
          all: rows.length,
          renew: rows.filter(r => r.decision === 'renew' && !r.ended_at).length,
          end: rows.filter(r => r.decision === 'end' && !r.ended_at).length,
          ended: rows.filter(r => !!r.ended_at).length,
        };
        const CARDS: Array<{ k: 'all'|'renew'|'end'|'ended'; label: string; color: string }> = [
          { k: 'all',   label: '전체 계약',      color: 'var(--text)' },
          { k: 'renew', label: '갱신 (진행중)',  color: 'var(--leaf-700)' },
          { k: 'end',   label: '종료 예정/대기', color: '#c2410c' },
          { k: 'ended', label: '계약 종료 완료', color: 'var(--text-muted)' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
            {CARDS.map(c => {
              const active = filter === c.k;
              return (
                <button key={c.k}
                        onClick={() => setFilter(c.k)}
                        style={{
                          padding: '12px 14px', border: '1px solid var(--line, #e6ece9)',
                          borderRadius: 8, background: active ? '#f0fdf4' : '#fff',
                          cursor: 'pointer', textAlign: 'left',
                          outline: active ? '2px solid var(--leaf-700)' : 'none',
                        }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{cnts[c.k]}</div>
                </button>
              );
            })}
          </div>
        );
      })()}

      <Card styles={{ body: { padding: 12 } }} style={{ marginBottom: 12 }}>
        <Space wrap size={8}>
          <Select size="small" value={filter} onChange={setFilter} style={{ width: 140 }}
            options={[
              { value: 'all', label: '전체' },
              { value: 'renew', label: '갱신 (진행)' },
              { value: 'end', label: '종료 예정/대기' },
              { value: 'ended', label: '종료 완료' },
            ]} />
          <Input.Search placeholder="가맹점명/MID 검색" size="small" allowClear
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ width: 240 }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            총 {filtered.length}건 / 전체 {rows.length}건
          </Text>
        </Space>
      </Card>

      <Card styles={{ body: { padding: 0 } }}>
        <Table<Row>
          rowKey="mcht_id"
          dataSource={filtered}
          columns={cols}
          loading={loading}
          size="small"
          pagination={{ pageSize: 30, showSizeChanger: false }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editing ? `결정 수정 — ${editing.merchant_name}` : ''}
        open={!!editing}
        onCancel={closeEdit}
        onOk={saveEdit}
        confirmLoading={saving}
        okText="저장"
        cancelText="취소"
        width={520}
      >
        {editing && (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {/* 판단에 필요한 현재 상태를 우선 표시 */}
            <Alert type="info" showIcon style={{ fontSize: 12 }}
                   message={
                     <span>
                       <b>현재 결정</b>: {editing.decision === 'end' ? '종료' : '갱신'}
                       {' · '}
                       <b>당일 잔여액</b>: <span style={{ color: editing.remaining_amount > 0 ? '#c2410c' : 'var(--leaf-700)', fontWeight: 700 }}>
                         {fmt.won(editing.remaining_amount)}원
                       </span>
                       {' · '}
                       <b>시작일</b>: {editing.start_date}
                       {' · '}
                       <b>현재 종료일</b>: {editing.contract_end_date}
                     </span>
                   } />
            <div>
              <Text strong style={{ fontSize: 12 }}>결정</Text>
              <Select value={editDecision} onChange={setEditDecision}
                      style={{ width: '100%', marginTop: 4 }}
                      options={[
                        { value: 'renew', label: '갱신 (계속 운영, 기본)' },
                        { value: 'end',   label: '종료 (잔여 0 시점에 자동 종료)' },
                      ]} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <Text strong style={{ fontSize: 12 }}>계약 이용기간 (일)</Text>
                <Input type="number" min={1} value={editTermDays || ''}
                       onChange={e => {
                         const t = Number(e.target.value) || 0;
                         setEditTermDays(t);
                         // 시작일 + 이용기간 → 종료일 자동 갱신
                         if (editing?.start_date && t > 0) {
                           setEditEndDate(computeEndDate(editing.start_date, t));
                         }
                       }}
                       placeholder="예: 30"
                       style={{ marginTop: 4 }} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  시작일 포함. 예) 시작일 = {editing.start_date}, 30일 → 종료일 자동 계산.
                </div>
              </div>
              <div>
                <Text strong style={{ fontSize: 12 }}>종료 예정일 (계약 종료일)</Text>
                <Input type="date" value={editEndDate}
                       onChange={e => {
                         const v = e.target.value;
                         setEditEndDate(v);
                         // 종료일 직접 변경 시 이용기간 역산
                         if (editing?.start_date && v) {
                           setEditTermDays(computeTermDays(editing.start_date, v));
                         }
                       }}
                       min={editing.start_date}
                       style={{ marginTop: 4 }} />
                {editEndDate && editEndDate < editing.start_date && (
                  <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 4 }}>
                    종료일은 시작일({editing.start_date})보다 빠를 수 없습니다.
                  </div>
                )}
              </div>
            </div>
            <div>
              <Text strong style={{ fontSize: 12 }}>
                사유
                {editDecision === 'end' && <Text type="warning" style={{ fontSize: 11, marginLeft: 6 }}>(종료 선택 시 권장)</Text>}
              </Text>
              <Input.TextArea value={editReason} onChange={e => setEditReason(e.target.value)}
                              autoSize={{ minRows: 3, maxRows: 6 }} maxLength={500} showCount
                              placeholder={editDecision === 'end'
                                ? '예: 계약기간 종료 및 잔여액 0원 확인'
                                : '예: 재계약 협의 중으로 계약기간 연장'}
                              style={{ marginTop: 4 }} />
            </div>
            {editDecision === 'end' && editing.remaining_amount > 0 && (
              <Alert type="warning" showIcon style={{ fontSize: 12 }}
                     message={`잔여액이 ${fmt.won(editing.remaining_amount)} 남아 있어 자동 종료 대상이 아닙니다. 잔여 0이 될 때까지 자동으로 대기합니다.`} />
            )}
            <Alert type="info" showIcon style={{ fontSize: 12 }}
                   message={
                     editDecision === 'end'
                       ? `종료일 ${editEndDate} 도래 + 잔여 0 시점에 자동 종료 처리됩니다. 잔여가 있으면 그대로 두고 잔여 0 될 때까지 대기.`
                       : '갱신 = 계속 운영. 종료일이 지나도 별도 처리 안 됨.'
                   } />
          </Space>
        )}
      </Modal>

      {/* 자동 종료 미리보기 — 대상/스킵 목록 사전 확인 후 실행 */}
      <Modal
        title={preview ? `자동 종료 대상 확인 — 기준일 ${preview.today}` : ''}
        open={!!preview}
        onCancel={() => setPreview(null)}
        onOk={runAutoEnd}
        okText={preview ? `대상 ${preview.eligible_count}건 자동 종료 실행` : '실행'}
        okButtonProps={{ disabled: !preview || preview.eligible_count === 0, danger: true }}
        cancelText="취소"
        confirmLoading={autoRunning}
        width={780}
      >
        {preview && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert type={preview.eligible_count > 0 ? 'warning' : 'info'} showIcon
                   message={
                     preview.eligible_count > 0
                       ? `자동 종료 대상 ${preview.eligible_count}건 · 조건 미충족(잔여 남음) ${preview.skipped_count}건.\n[실행]을 누르면 대상 ${preview.eligible_count}건의 계약이 즉시 종료됩니다.`
                       : `자동 종료 조건(종료일 도래 + 잔여액 0 + 종료 결정)을 충족하는 가맹점이 없습니다.`
                   }
                   style={{ whiteSpace: 'pre-line' }} />
            {preview.eligible_count > 0 && (
              <div>
                <Text strong style={{ fontSize: 12 }}>자동 종료 대상 ({preview.eligible_count}건)</Text>
                <Table<PreviewCandidate>
                  size="small" rowKey="mcht_id"
                  dataSource={preview.eligible} pagination={false}
                  scroll={{ y: 220 }}
                  columns={[
                    { title: 'MID', dataIndex: 'mcht_id', width: 130,
                      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                    { title: '가맹점명', dataIndex: 'merchant_name', ellipsis: true },
                    { title: '계약 종료 예정일', dataIndex: 'contract_end_date', width: 130,
                      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                    { title: '당일 잔여액', dataIndex: 'remaining_amount', align: 'right', width: 120,
                      render: v => <Text style={{ color: 'var(--leaf-700)' }}>{fmt.won(v)}원</Text> },
                  ]}
                />
              </div>
            )}
            {preview.skipped_count > 0 && (
              <div>
                <Text strong style={{ fontSize: 12 }}>조건 미충족 (실행 시 제외됨, {preview.skipped_count}건)</Text>
                <Table<PreviewCandidate>
                  size="small" rowKey="mcht_id"
                  dataSource={preview.skipped} pagination={false}
                  scroll={{ y: 160 }}
                  columns={[
                    { title: 'MID', dataIndex: 'mcht_id', width: 130,
                      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                    { title: '가맹점명', dataIndex: 'merchant_name', ellipsis: true },
                    { title: '계약 종료 예정일', dataIndex: 'contract_end_date', width: 130,
                      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                    { title: '당일 잔여액', dataIndex: 'remaining_amount', align: 'right', width: 120,
                      render: v => <Text type="warning">{fmt.won(v)}원</Text> },
                    { title: '제외 사유', dataIndex: 'skip_reason', ellipsis: true,
                      render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
                  ]}
                />
              </div>
            )}
          </Space>
        )}
      </Modal>

      {/* 자동 종료 실행 결과 요약 */}
      <Modal
        title={runResult ? `자동 종료 실행 결과 — ${runResult.today}` : ''}
        open={!!runResult}
        onCancel={() => setRunResult(null)}
        footer={<Button onClick={() => setRunResult(null)}>닫기</Button>}
        width={640}
      >
        {runResult && (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Alert
              type={runResult.ended_count > 0 ? 'success' : 'info'}
              showIcon
              message={
                `총 대상 ${runResult.ended_count + runResult.skipped_count}건 · ` +
                `정상 종료 ${runResult.ended_count}건 · 제외 ${runResult.skipped_count}건`
              }
              description={`실행 주체: ${runResult.triggered_by}`}
            />
            {runResult.ended.length > 0 && (
              <div>
                <Text strong style={{ fontSize: 12 }}>종료 완료 MID</Text>
                <div style={{ fontFamily: 'monospace', fontSize: 12, background: '#f8fafc', padding: 10, borderRadius: 6, maxHeight: 180, overflow: 'auto' }}>
                  {runResult.ended.join(', ')}
                </div>
              </div>
            )}
            {runResult.skipped.length > 0 && (
              <div>
                <Text strong style={{ fontSize: 12 }}>제외 (사유별)</Text>
                <Table
                  size="small" rowKey="mid"
                  dataSource={runResult.skipped} pagination={false}
                  scroll={{ y: 180 }}
                  columns={[
                    { title: 'MID', dataIndex: 'mid', width: 140,
                      render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                    { title: '사유', dataIndex: 'reason', ellipsis: true },
                  ]}
                />
              </div>
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title={eventsFor ? `이벤트 이력 — ${eventsFor.merchant_name}` : ''}
        open={!!eventsFor}
        onCancel={() => setEventsFor(null)}
        footer={null}
        width={680}
      >
        {events.length === 0 ? (
          <Text type="secondary">이력 없음</Text>
        ) : (
          <Table
            rowKey="id"
            size="small"
            dataSource={events}
            pagination={false}
            columns={[
              { title: '시각', dataIndex: 'created_at', key: 'at', width: 160 },
              { title: '유형', dataIndex: 'event_type', key: 'type', width: 90,
                render: (v: string) => v === 'end' ? <Tag color="orange">종료</Tag> : <Tag>{v}</Tag>
              },
              { title: '발생일', dataIndex: 'event_date', key: 'date', width: 110 },
              { title: '주체', dataIndex: 'triggered_by', key: 'by', width: 80 },
              { title: '사유', dataIndex: 'reason', key: 'reason', ellipsis: true },
            ]}
          />
        )}
      </Modal>
    </div>
  );
}
